/**
 * Column widths a viewer has dragged a table to, remembered per table in this browser.
 *
 * One localStorage key per table (`cpc.colw.<table>`), holding versioned JSON of only the
 * columns the viewer resized: the rest keep sizing themselves from the data. A per-viewer
 * convenience, so every read and write survives blocked storage (a private window, cleared
 * site data) by forgetting rather than failing — the table then draws at its defaults.
 */

/** Narrowest a column may be dragged to: wide enough to grab again. */
export const MIN_COL_WIDTH = 40

const PREFIX = 'cpc.colw.'
const VERSION = 1

export type ColumnWidths = Record<string, number>

export const columnWidthsKey = (table: string) => PREFIX + table

/** Whatever was stored, reduced to finite widths at or above the floor. Garbage reads as none. */
export function parseColumnWidths(raw: string | null): ColumnWidths {
  if (!raw) return {}
  try {
    const v = JSON.parse(raw) as { v?: unknown; widths?: unknown }
    if (v?.v !== VERSION || !v.widths || typeof v.widths !== 'object') return {}
    const out: ColumnWidths = {}
    for (const [k, w] of Object.entries(v.widths as Record<string, unknown>))
      if (typeof w === 'number' && Number.isFinite(w)) out[k] = Math.max(MIN_COL_WIDTH, Math.round(w))
    return out
  } catch {
    return {}
  }
}

export function readColumnWidths(table: string): ColumnWidths {
  try {
    return parseColumnWidths(localStorage.getItem(columnWidthsKey(table)))
  } catch {
    return {}
  }
}

/** Saves the table's widths; none at all removes the key, so a reset leaves nothing behind. */
export function writeColumnWidths(table: string, widths: ColumnWidths): void {
  try {
    if (Object.keys(widths).length === 0) localStorage.removeItem(columnWidthsKey(table))
    else localStorage.setItem(columnWidthsKey(table), JSON.stringify({ v: VERSION, widths }))
  } catch {
    /* storage blocked: not remembered */
  }
}

export function resetColumnWidths(table: string): void {
  writeColumnWidths(table, {})
}
